import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Server, Database, Globe, Smartphone, 
  ExternalLink, CheckCircle, AlertCircle, Clock,
  Eye, EyeOff, RefreshCw, Shield
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { api } from '../../lib/api';

const AdminInfrastructure = () => {
  const navigate = useNavigate();
  const [healthStatus, setHealthStatus] = useState({
    backend: { status: 'checking', lastCheck: null },
  });
  const [showTestCreds, setShowTestCreds] = useState(false);
  const [checking, setChecking] = useState(false);

  const checkHealth = async () => {
    setChecking(true);
    try {
      const response = await api.get('/health');
      setHealthStatus({
        backend: { 
          status: response.data.status === 'ok' ? 'healthy' : 'unhealthy',
          lastCheck: new Date().toLocaleTimeString()
        }
      });
    } catch (error) {
      setHealthStatus({
        backend: { 
          status: 'unhealthy',
          lastCheck: new Date().toLocaleTimeString()
        }
      });
    }
    setChecking(false);
  };

  useEffect(() => {
    checkHealth();
    const interval = setInterval(checkHealth, 30000); // Check every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const StatusBadge = ({ status }) => {
    const config = {
      healthy: { color: 'bg-green-100 text-green-700', icon: CheckCircle, label: 'Healthy' },
      unhealthy: { color: 'bg-red-100 text-red-700', icon: AlertCircle, label: 'Unhealthy' },
      checking: { color: 'bg-yellow-100 text-yellow-700', icon: Clock, label: 'Checking...' },
      pending: { color: 'bg-yellow-100 text-yellow-700', icon: Clock, label: 'Pending' },
      live: { color: 'bg-green-100 text-green-700', icon: CheckCircle, label: 'Live' },
    };
    const { color, icon: Icon, label } = config[status] || config.checking;
    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${color}`}>
        <Icon className="w-3.5 h-3.5" />
        {label}
      </span>
    );
  };

  const QuickLink = ({ href, icon: Icon, label, description }) => (
    <a 
      href={href} 
      target="_blank" 
      rel="noopener noreferrer"
      className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors group"
    >
      <div className="p-2 bg-white rounded-lg shadow-sm">
        <Icon className="w-5 h-5 text-[#1C3A7A]" />
      </div>
      <div className="flex-1">
        <p className="font-medium text-gray-900">{label}</p>
        <p className="text-xs text-gray-500">{description}</p>
      </div>
      <ExternalLink className="w-4 h-4 text-gray-400 group-hover:text-[#1C3A7A]" />
    </a>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/admin')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <Shield className="w-5 h-5 text-[#1C3A7A]" />
              Infrastructure
            </h1>
            <p className="text-sm text-gray-500">System overview & quick links (Super Admin only)</p>
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={checkHealth}
            disabled={checking}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${checking ? 'animate-spin' : ''}`} />
            Refresh Status
          </Button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-6 space-y-6">
        
        {/* Architecture Diagram */}
        <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
          <div className="p-4 border-b bg-gradient-to-r from-[#1C3A7A] to-[#2a4d9e]">
            <h2 className="text-lg font-semibold text-white">Architecture Overview</h2>
          </div>
          <div className="p-6 flex justify-center bg-slate-900">
            <img 
              src="/images/architecture-diagram.png" 
              alt="RecoveryMatch Infrastructure"
              className="max-w-full h-auto rounded-lg"
              style={{ maxHeight: '600px' }}
            />
          </div>
        </div>

        {/* Live System Status */}
        <div className="bg-white rounded-2xl shadow-sm border p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Server className="w-5 h-5 text-[#1C3A7A]" />
            Live System Status
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Frontend */}
            <div className="p-4 border rounded-xl">
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium text-gray-900">Frontend</span>
                <StatusBadge status="live" />
              </div>
              <p className="text-sm text-gray-500">recoverymatch.com</p>
              <p className="text-xs text-gray-400 mt-1">Render Static Site</p>
            </div>
            
            {/* Backend */}
            <div className="p-4 border rounded-xl">
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium text-gray-900">Backend API</span>
                <StatusBadge status={healthStatus.backend.status} />
              </div>
              <p className="text-sm text-gray-500">api.recoverymatch.com</p>
              <p className="text-xs text-gray-400 mt-1">
                Last check: {healthStatus.backend.lastCheck || 'Never'}
              </p>
            </div>
            
            {/* Database */}
            <div className="p-4 border rounded-xl">
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium text-gray-900">Database</span>
                <StatusBadge status={healthStatus.backend.status === 'healthy' ? 'healthy' : 'checking'} />
              </div>
              <p className="text-sm text-gray-500">MongoDB Atlas</p>
              <p className="text-xs text-gray-400 mt-1">RecoveryMatch-Cluster</p>
            </div>
            
            {/* iOS App */}
            <div className="p-4 border rounded-xl">
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium text-gray-900">iOS App</span>
                <StatusBadge status="live" />
              </div>
              <p className="text-sm text-gray-500">v1.0.2 (Build 157)</p>
              <p className="text-xs text-gray-400 mt-1">App Store</p>
            </div>
            
            {/* Android App */}
            <div className="p-4 border rounded-xl">
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium text-gray-900">Android App</span>
                <StatusBadge status="live" />
              </div>
              <p className="text-sm text-gray-500">v1.0.2 (Build 157)</p>
              <p className="text-xs text-gray-400 mt-1">Play Store</p>
            </div>
            
            {/* AI Services */}
            <div className="p-4 border rounded-xl">
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium text-gray-900">AI Services</span>
                <StatusBadge status="live" />
              </div>
              <p className="text-sm text-gray-500">OpenAI GPT-4o</p>
              <p className="text-xs text-gray-400 mt-1">Bio Assistant & Chat Starters</p>
            </div>
          </div>
        </div>

        {/* Quick Links */}
        <div className="bg-white rounded-2xl shadow-sm border p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <ExternalLink className="w-5 h-5 text-[#1C3A7A]" />
            Quick Links
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <QuickLink 
              href="https://dashboard.render.com" 
              icon={Server} 
              label="Render Dashboard"
              description="Frontend & Backend hosting"
            />
            <QuickLink 
              href="https://cloud.mongodb.com" 
              icon={Database} 
              label="MongoDB Atlas"
              description="Database management"
            />
            <QuickLink 
              href="https://github.com/tperrin101/RecoveryMatch" 
              icon={Globe} 
              label="GitHub Repository"
              description="Source code"
            />
            <QuickLink 
              href="https://platform.openai.com" 
              icon={Server} 
              label="OpenAI Dashboard"
              description="AI usage & billing"
            />
            <QuickLink 
              href="https://appstoreconnect.apple.com" 
              icon={Smartphone} 
              label="App Store Connect"
              description="iOS app management"
            />
            <QuickLink 
              href="https://play.google.com/console" 
              icon={Smartphone} 
              label="Google Play Console"
              description="Android app management"
            />
          </div>
        </div>

        {/* Environment Info */}
        <div className="bg-white rounded-2xl shadow-sm border p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Globe className="w-5 h-5 text-[#1C3A7A]" />
            Environment Info
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-3">
              <div className="flex justify-between py-2 border-b">
                <span className="text-gray-500">Frontend Host</span>
                <span className="font-medium">Render Static Site</span>
              </div>
              <div className="flex justify-between py-2 border-b">
                <span className="text-gray-500">Backend Host</span>
                <span className="font-medium">Render Web Service</span>
              </div>
              <div className="flex justify-between py-2 border-b">
                <span className="text-gray-500">Database</span>
                <span className="font-medium">MongoDB Atlas</span>
              </div>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between py-2 border-b">
                <span className="text-gray-500">Current Version</span>
                <span className="font-medium">1.0.2</span>
              </div>
              <div className="flex justify-between py-2 border-b">
                <span className="text-gray-500">Build Number</span>
                <span className="font-medium">157</span>
              </div>
              <div className="flex justify-between py-2 border-b">
                <span className="text-gray-500">API Framework</span>
                <span className="font-medium">FastAPI (Python)</span>
              </div>
            </div>
          </div>
        </div>

        {/* Test Credentials */}
        <div className="bg-white rounded-2xl shadow-sm border p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Shield className="w-5 h-5 text-[#1C3A7A]" />
              Test Credentials
            </h2>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setShowTestCreds(!showTestCreds)}
            >
              {showTestCreds ? (
                <>
                  <EyeOff className="w-4 h-4 mr-2" />
                  Hide
                </>
              ) : (
                <>
                  <Eye className="w-4 h-4 mr-2" />
                  Show
                </>
              )}
            </Button>
          </div>
          
          {showTestCreds ? (
            <div className="space-y-4">
              <div className="p-4 bg-blue-50 rounded-xl">
                <h3 className="font-medium text-gray-900 mb-2">Seed User Accounts</h3>
                <div className="space-y-1 text-sm">
                  <p><span className="text-gray-500">Emails:</span> member1@recoverymatch.com through member100@recoverymatch.com</p>
                  <p><span className="text-gray-500">Password:</span> <code className="bg-white px-2 py-0.5 rounded">stored in the team password manager</code></p>
                </div>
              </div>
              <p className="text-xs text-gray-500">
                These accounts were created for testing purposes. Use them to test matching, messaging, and other features.
              </p>
            </div>
          ) : (
            <p className="text-sm text-gray-500">
              Click "Show" to reveal test account credentials.
            </p>
          )}
        </div>

        {/* Domain Mapping */}
        <div className="bg-white rounded-2xl shadow-sm border p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Globe className="w-5 h-5 text-[#1C3A7A]" />
            Domain Mapping
          </h2>
          <div className="space-y-2">
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <code className="text-sm font-mono text-[#1C3A7A]">recoverymatch.com</code>
              <span className="text-gray-400">→</span>
              <span className="text-sm text-gray-600">Frontend (Render)</span>
            </div>
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <code className="text-sm font-mono text-[#1C3A7A]">www.recoverymatch.com</code>
              <span className="text-gray-400">→</span>
              <span className="text-sm text-gray-600">Frontend (Render)</span>
            </div>
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <code className="text-sm font-mono text-[#1C3A7A]">api.recoverymatch.com</code>
              <span className="text-gray-400">→</span>
              <span className="text-sm text-gray-600">Backend API (Render)</span>
            </div>
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <code className="text-sm font-mono text-[#1C3A7A]">recoverymatch.com/admin</code>
              <span className="text-gray-400">→</span>
              <span className="text-sm text-gray-600">Admin Panel</span>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default AdminInfrastructure;
